import React, { useEffect, useMemo, useRef, useState } from "react";
import { SearchIcon } from "@/componentsUI/Icon";
import { useTranslationPlus } from "@/Core/i18n/useTranslationPlus";
import InputUI from "@/componentsUI/Input";
import PopupMask from "@/componentsUI/PopupMask";
import styles from "./index.module.less";

export interface Bank {
  name: string;
  code: string;
  nipBankCode?: string;
}

export interface BankSelectorProps {
  value?: Bank;
  onChange?: (bank: Bank) => void;
  placeholder?: string;
  disabled?: boolean;
  withdrawType?: number;
  onWithdrawTypeChange?: (type: number) => void;
  availableChannels?: number[];
  bankCodeDataByChannel?: {
    102?: any[];
    104?: any[];
  };
}

const BankSelector: React.FC<BankSelectorProps> = ({
  value,
  onChange,
  placeholder,
  disabled = false,
  withdrawType = 102,
  onWithdrawTypeChange,
  availableChannels = [102, 104],
  bankCodeDataByChannel,
}) => {
  const { t } = useTranslationPlus("Wallet");
  const [modalVisible, setModalVisible] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [activeTab, setActiveTab] = useState<number>(withdrawType);
  const [bankCodeData102, setBankCodeData102] = useState<any[] | null>(null);

  React.useEffect(() => {
    if (!availableChannels.length) {
      setActiveTab(withdrawType);
      return;
    }
    if (availableChannels.includes(withdrawType)) {
      setActiveTab(withdrawType);
      return;
    }
    setActiveTab(availableChannels[0]);
  }, [availableChannels, withdrawType]);

  useEffect(() => {
    if (
      bankCodeData102 ||
      activeTab !== 102 ||
      !availableChannels.includes(102) ||
      bankCodeDataByChannel?.[102]?.length
    ) {
      return;
    }
    let isMounted = true;
    import(
      /* webpackChunkName: "bankcode-102" */
      "../../assets/ng/Bankcode-102.json"
    )
      .then((mod) => {
        if (isMounted) {
          setBankCodeData102(mod.default || mod);
        }
      })
      .catch((error) => {
        console.error("Failed to load Bankcode-102:", error);
      });
    return () => {
      isMounted = false;
    };
  }, [activeTab, bankCodeData102, bankCodeDataByChannel]);

  useEffect(() => {
    if (!modalVisible) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        handleModalClose();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [modalVisible]);

  const normalizedBankData = useMemo(() => {
    const rawData =
      activeTab === 102
        ? bankCodeDataByChannel?.[102] || bankCodeData102 || []
        : bankCodeDataByChannel?.[104] || [];

    if (activeTab === 102) {
      return rawData.map((bank: any) => ({
        name: bank.name,
        code: bank.code,
        nipBankCode: bank.nipBankCode,
      }));
    }
    return rawData.map((bank: any) => ({
      name: bank.bankName,
      code: bank.bankCode,
      nipBankCode: bank.bankCode,
    }));
  }, [activeTab, bankCodeDataByChannel]);

  const filteredBanks = useMemo(() => {
    if (!searchValue.trim()) {
      return normalizedBankData;
    }
    return normalizedBankData.filter((bank) =>
      bank.name.toLowerCase().includes(searchValue.toLowerCase())
    );
  }, [searchValue, normalizedBankData]);

  const handleBankSelect = (bank: Bank) => {
    onChange?.(bank);
    setModalVisible(false);
    setSearchValue("");
  };

  const handleModalClose = () => {
    setModalVisible(false);
    setSearchValue("");
  };

  const handleTabChange = (type: number) => {
    setActiveTab(type);
    setSearchValue("");
    onWithdrawTypeChange?.(type);
  };

  return (
    <div className={styles.bankSelector}>
      <div
        className={`${styles.selectorInput} ${disabled ? styles.disabled : ""}`}
        onClick={() => !disabled && setModalVisible(true)}
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={(event) => {
          if (disabled) {
            return;
          }
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            setModalVisible(true);
          }
        }}
      >
        <span className={styles.selectedBank}>
          {value ? value.name : placeholder || t("Select Bank")}
        </span>
        <span className={styles.arrow}>▼</span>
      </div>

      <PopupMask visible={modalVisible} onClose={handleModalClose}>
        <div
          className={styles.bankPanel}
          role="dialog"
          aria-modal="true"
          aria-label={t("Select Bank")}
        >
          <div className={styles.panelHeader}>
            <div className={styles.panelTitle}>{t("Select Bank")}</div>
            <button
              type="button"
              className={styles.closeButton}
              onClick={handleModalClose}
              aria-label={t("Select Bank")}
            >
              ×
            </button>
          </div>

          <div className={styles.modalContent}>
            {availableChannels.length > 1 && (
              <div className={styles.tabSection}>
                <div className={styles.tabContainer}>
                  {availableChannels.includes(102) && (
                    <div
                      className={`${styles.tabItem} ${
                        activeTab === 102 ? styles.active : ""
                      }`}
                      onClick={() => handleTabChange(102)}
                    >
                      {t("Channel 1")}
                    </div>
                  )}
                  {availableChannels.includes(104) && (
                    <div
                      className={`${styles.tabItem} ${
                        activeTab === 104 ? styles.active : ""
                      }`}
                      onClick={() => handleTabChange(104)}
                    >
                      {t("Channel 2")}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className={styles.searchSection}>
              <InputUI
                placeholder={t("Search bank name")}
                value={searchValue}
                onChange={setSearchValue}
                prefix={<SearchIcon />}
                allowClear
                className={styles.searchInput}
              />
            </div>

            <div className={styles.bankList}>
              {filteredBanks.length > 0 ? (
                filteredBanks.map((bank, index) => (
                  <div
                    key={`${activeTab}-${bank.code}-${index}`}
                    className={`${styles.bankItem} ${
                      value?.code === bank.code ? styles.selected : ""
                    }`}
                    onClick={() => handleBankSelect(bank)}
                  >
                    <div className={styles.bankName}>{bank.name}</div>
                    <div className={styles.bankCode}>
                      {t("Code")}: {bank.code}
                    </div>
                  </div>
                ))
              ) : (
                <div className={styles.noResults}>
                  {t("No banks found matching your search")}
                </div>
              )}
            </div>
          </div>
        </div>
      </PopupMask>
    </div>
  );
};

export default BankSelector;
